import BtsLogo from '../assets/bts_logo.svg?react';
import { TreeView } from '@progress/kendo-react-treeview';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { ButtonGroup } from '@progress/kendo-react-buttons';
import { HeroIcon } from '../shared/HeroIcon.tsx';
import { AdminMenuItem, AdminMenuItemProps } from './AdminMenuItem.tsx';
import { useUserCompanies } from './hooks/useUserCompanies.ts';
import { expandMenuItem } from './menu.ts';
import { loadMessages } from '@progress/kendo-react-intl';
import deMessages from './translation/de-DE.json';
import React, { useCallback, useEffect, useState } from 'react';
import { useUserInfo } from '../public/hooks/useUserInfo.ts';
import { ModulePrivileges } from '../gql/graphql.ts';
import { useKeycloak } from '../shared/auth/KeycloakProvider.tsx';
import { MakeAWishModal } from './MakeAWishModal.tsx';
import { restApiUrl } from '../shared/helpers/backendUrl';

loadMessages(deMessages, 'de-DE');

interface IAdminLayoutProps {
  children?: React.ReactNode;
  companyId: string;
}

export const AdminLayout: React.FC<IAdminLayoutProps> = ({
  children,
  companyId,
}) => {
  const location = useLocation({ select: (l) => l.pathname });
  const navigate = useNavigate();
  const { menu, companies, setMenu } = useUserCompanies();
  const { userInfo } = useUserInfo();
  const { logout, login, authenticated } = useKeycloak();
  const [wishModalOpen, setWishModalOpen] = useState(false);

  // Inject the action handlers into each menu item. Memoized so the TreeView
  // keeps a stable item renderer and doesn't remount the menu on every render.
  const renderMenuItem = useCallback(
    (props: AdminMenuItemProps) => (
      <AdminMenuItem {...props} onClick={() => setWishModalOpen(true)} />
    ),
    [],
  );

  useEffect(() => {
    if (!authenticated) {
      login(window.location.href).catch(() => {
        window.location.href = restApiUrl(
          `/auth/login?successRef=${encodeURIComponent(window.location.href)}`,
        );
      });
    }
  }, [authenticated, login]);

  return companies ? (
    <div className="grid h-full grid-cols-[300px_1fr] grid-rows-[70px_1fr]">
      <title>{`Admin Dashboard - ${import.meta.env.VITE_APP_TITLE}`}</title>
      <div className="z-10 col-span-2 flex items-center justify-between border-b bg-white p-4 shadow-md">
        <Link to="/" className="h-full w-auto" title="Zur Startseite">
          <BtsLogo className="h-full w-auto text-coral-400" />
        </Link>
        <div className="flex items-center gap-4">
          {companies && (
            <span>
              Hallo, {userInfo?.data?.currentUser?.address?.firstName}
            </span>
          )}
          <ButtonGroup>
            {companies?.length && (
              <DropDownList
                className={`!w-64 !rounded-r-none !border-r-0 !border-[#6c757d] !text-[#6c757d] hover:!bg-[#6c757d] hover:!text-white ${
                  companies?.length < 2 ? '[&_button]:invisible' : ''
                }`}
                // Append the popup inside #root so Tailwind utilities
                // (which are scoped to #root via `important`) apply to
                // the rendered list items — otherwise the icon below
                // ignores its size class and renders huge.
                popupSettings={{
                  appendTo: document.getElementById('root') ?? undefined,
                }}
                textField="name"
                dataItemKey="id"
                fillMode={'outline'}
                itemRender={(
                  li: React.ReactElement<HTMLLIElement>,
                  itemProps,
                ) => {
                  const itemChildren = (
                    <span className="flex items-center gap-1">
                      {itemProps.dataItem.icon && (
                        <HeroIcon
                          name={itemProps.dataItem.icon}
                          className="inline-block size-5 text-zinc-500"
                        />
                      )}
                      {li.props.children as React.ReactNode}
                    </span>
                  );

                  return React.cloneElement(li, li.props, itemChildren);
                }}
                data={
                  userInfo?.data?.currentUser?.privileges?.includes(
                    ModulePrivileges.Tenants,
                  )
                    ? [
                        ...companies,
                        {
                          id: -1,
                          name: 'Seminarhaus anlegen',
                          icon: 'PlusCircle',
                        },
                      ]
                    : companies
                }
                disabled={companies?.length < 2}
                value={companies?.find((company) => company.id === companyId)}
                onChange={(e) => {
                  if (e.target.value.id === -1) {
                    navigate({ to: '/admin/company/new' });
                  } else {
                    const newCompanyId = e.target.value.id;

                    const newCompany = companies.find(
                      (company) => company.id === newCompanyId,
                    );

                    if (newCompany) {
                      const newTarget = `${newCompany.domain.startsWith('http') ? '' : 'https://'}${newCompany.domain}${location}`;
                      window.location.href = newTarget;
                    }
                  }
                }}
              />
            )}
            <button
              onClick={() =>
                void logout().catch((err) =>
                  console.error('[Auth] Logout failed:', err),
                )
              }
              className="k-button k-button-md k-button-outline k-button-outline-secondary k-rounded-md text-red"
            >
              <HeroIcon
                name={'ArrowLeftStartOnRectangle'}
                className="inline-block h-5 w-5"
              />
              Abmelden
            </button>
          </ButtonGroup>
        </div>
      </div>
      <aside className="flex h-full flex-col border-r bg-zinc-100 p-4">
        <div className="flex-1 overflow-y-auto">
          <TreeView
            size={'large'}
            // className="[&_.k-treeview-toggle]:text-coral-400"
            expandIcons={true}
            data={menu}
            className="[&_.k-disabled]:!opacity-35 [&_.k-selected]:!bg-white [&_.k-selected_a]:!font-semibold"
            onExpandChange={(e) => {
              const newMenu = expandMenuItem(e, menu);
              setMenu(newMenu);
            }}
            item={renderMenuItem}
          />
        </div>
      </aside>
      {wishModalOpen && (
        <MakeAWishModal onClose={() => setWishModalOpen(false)} />
      )}
      {/* Main Content */}
      <div className="h-full bg-zinc-100 px-8 py-4">{children}</div>
    </div>
  ) : null;
};
